//  Holographic Projection Hero  OpticVance 
import React, {
  useRef, useState, useEffect, useCallback, useMemo,
} from 'react';
import {
  motion, useMotionValue, useTransform, useSpring, AnimatePresence,
} from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';

/* 
   CONSTANTS
    */
const GREEN_A  = '#08CB00';
const GREEN_B  = '#54E346';
const HEADLINE = 'Revolutionize Analytics with AI Power';
const SUBHEAD  = 'OpticVance turns raw data into strategic foresight  autonomously, in real time.';
const mob = () => typeof window !== 'undefined' && window.innerWidth < 768;

/* 
   INFINITY GRID FLOOR  perspective CSS grid
    */
const GridFloor = ({ tiltX, tiltY }) => (
  <motion.div
    className="absolute inset-0 overflow-hidden pointer-events-none z-0"
    style={{ perspective: 600, perspectiveOrigin: '50% 95%' }}
    aria-hidden="true"
  >
    <motion.div
      className="absolute w-[200%] h-[200%] left-[-50%]"
      style={{
        top: '48%',
        rotateX: useTransform(tiltY, v => 72 + v * 0.03),
        rotateZ: useTransform(tiltX, v => v * 0.008),
        backgroundImage:
          `linear-gradient(rgba(8,203,0,0.08) 1px, transparent 1px),
           linear-gradient(90deg, rgba(8,203,0,0.08) 1px, transparent 1px)`,
        backgroundSize: '56px 56px',
      }}
    />
    {/* horizon glow */}
    <div
      className="absolute left-0 right-0 h-32"
      style={{
        top: '44%',
        background: 'linear-gradient(180deg, transparent 0%, rgba(8,203,0,0.06) 40%, rgba(84,227,70,0.12) 70%, transparent 100%)',
        filter: 'blur(18px)',
      }}
    />
  </motion.div>
);

/* 
   GLOWING PYRAMID PEDESTAL
    */
const Pedestal = () => (
  <div className="relative w-full flex justify-center" aria-hidden="true">
    {/* Pyramid SVG */}
    <svg viewBox="0 0 320 100" className="w-96 sm:w-[30rem] lg:w-[40rem] h-auto relative z-10">
      <defs>
        <linearGradient id="pyGrad" x1="50%" y1="0%" x2="50%" y2="100%">
          <stop offset="0%"   stopColor={GREEN_B} stopOpacity="0.7" />
          <stop offset="100%" stopColor={GREEN_A} stopOpacity="0.15" />
        </linearGradient>
        <filter id="pyGlow">
          <feGaussianBlur in="SourceGraphic" stdDeviation="6" result="b" />
          <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
      </defs>
      {/* Outer pyramid */}
      <polygon
        points="160,8 40,92 280,92"
        fill="none" stroke="url(#pyGrad)" strokeWidth="1.5"
        filter="url(#pyGlow)"
      />
      {/* Inner pyramid */}
      <polygon
        points="160,28 80,86 240,86"
        fill="none" stroke={GREEN_B} strokeWidth="0.6" strokeOpacity="0.3"
      />
      {/* Base line */}
      <line x1="20" y1="94" x2="300" y2="94"
        stroke={GREEN_A} strokeWidth="2" strokeOpacity="0.4" />
      {/* Apex glow dot */}
      <circle cx="160" cy="8" r="3" fill={GREEN_B} opacity="0.8">
        <animate attributeName="r" values="3;5;3" dur="2s" repeatCount="indefinite" />
        <animate attributeName="opacity" values="0.8;0.4;0.8" dur="2s" repeatCount="indefinite" />
      </circle>
    </svg>

    {/* Scan-line sweep */}
    <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-96 sm:w-[30rem] lg:w-[40rem] h-full overflow-hidden pointer-events-none z-20">
      <div
        className="absolute left-0 right-0 h-px"
        style={{
          background: `linear-gradient(90deg, transparent 0%, ${GREEN_B} 50%, transparent 100%)`,
          boxShadow: `0 0 12px 3px rgba(84,227,70,0.5)`,
          animation: 'scanUp 2.4s ease-in-out infinite',
        }}
      />
    </div>

    {/* Floor reflection */}
    <div
      className="absolute -bottom-4 left-1/2 -translate-x-1/2 w-80 h-8 rounded-[50%]"
      style={{
        background: 'radial-gradient(ellipse, rgba(84,227,70,0.18) 0%, transparent 70%)',
        filter: 'blur(8px)',
      }}
    />
  </div>
);

/* 
   GLITCH TEXT  headline materialises with glitch layers
    */
const GlitchText = ({ text, as: Tag = 'h1', className = '', delay = 0 }) => {
  const [phase, setPhase] = useState('hidden');  // hidden  glitch  solid

  useEffect(() => {
    const t1 = setTimeout(() => setPhase('glitch'), delay);
    const t2 = setTimeout(() => setPhase('solid'),  delay + 900);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [delay]);

  if (phase === 'hidden') return <Tag className={`${className} opacity-0`}>{text}</Tag>;

  return (
    <Tag className={`${className} relative`}>
      {/* base */}
      <motion.span
        className="relative z-10"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
        style={{ color: '#ffffff' }}
      >
        {text}
      </motion.span>

      {/* glitch ghost layers */}
      {phase === 'glitch' && (
        <>
          <motion.span
            className="absolute inset-0 pointer-events-none select-none"
            style={{ color: GREEN_A, clipPath: 'inset(15% 0 60% 0)' }}
            animate={{ x: [-3, 4, -2, 0], opacity: [0.8, 1, 0.6, 0] }}
            transition={{ duration: 0.8, ease: 'easeOut' }}
            aria-hidden="true"
          >{text}</motion.span>
          <motion.span
            className="absolute inset-0 pointer-events-none select-none"
            style={{ color: GREEN_B, clipPath: 'inset(55% 0 10% 0)' }}
            animate={{ x: [5, -3, 1, 0], opacity: [0.7, 1, 0.5, 0] }}
            transition={{ duration: 0.8, ease: 'easeOut' }}
            aria-hidden="true"
          >{text}</motion.span>
        </>
      )}
    </Tag>
  );
};

/* 
   HOLOGRAPHIC DATA SHARDS  draggable, clickable  confetti
    */
const SHARD_DEFS = [];

/* Confetti particle burst */
const ConfettiBurst = ({ cx, cy, onDone }) => {
  const particles = useMemo(() =>
    Array.from({ length: 18 }, (_, i) => ({
      id: i,
      angle: (i / 18) * Math.PI * 2,
      dist:  40 + Math.random() * 60,
      size:  3 + Math.random() * 4,
      dur:   0.55 + Math.random() * 0.35,
      color: i % 2 === 0 ? GREEN_A : GREEN_B,
    })), []);

  useEffect(() => {
    const t = setTimeout(onDone, 1000);
    return () => clearTimeout(t);
  }, [onDone]);

  return (
    <div className="absolute inset-0 pointer-events-none z-50" aria-hidden="true">
      {particles.map(p => (
        <motion.div
          key={p.id}
          className="absolute rounded-full"
          style={{
            width: p.size, height: p.size,
            background: p.color,
            left: cx, top: cy,
            boxShadow: `0 0 6px ${p.color}`,
          }}
          initial={{ x: 0, y: 0, opacity: 1, scale: 1 }}
          animate={{
            x: Math.cos(p.angle) * p.dist,
            y: Math.sin(p.angle) * p.dist,
            opacity: 0, scale: 0.3,
          }}
          transition={{ duration: p.dur, ease: 'easeOut' }}
        />
      ))}
    </div>
  );
};

/* Single draggable shard */
const HoloShard = ({ def, index, isMob }) => {
  const [burst, setBurst]   = useState(null);
  const shardRef = useRef(null);

  const handleClick = useCallback((e) => {
    if (!shardRef.current) return;
    const r = shardRef.current.getBoundingClientRect();
    setBurst({ x: r.width / 2, y: r.height / 2 });
  }, []);

  const clearBurst = useCallback(() => setBurst(null), []);

  /* Desktop: float at scattered positions.  Mobile: stack vertically. */
  const positions = useMemo(() => [
    { x: '-42%', y: '-60%' },
    { x:  '44%', y: '-48%' },
    { x: '-52%', y:  '15%' },
    { x:  '50%', y:  '22%' },
    { x: '-30%', y:  '65%' },
    { x:  '35%', y:  '70%' },
  ], []);

  const pos = positions[index % positions.length];
  const floatDelay = index * 0.6;

  return (
    <motion.div
      ref={shardRef}
      className="relative cursor-grab active:cursor-grabbing select-none"
      style={{
        width: isMob ? 92 : 120,
        height: isMob ? 92 : 120,
      }}
      /* Desktop: absolute from centre.  Mobile: flow layout (no absolute). */
      {...(!isMob && {
        style: {
          position: 'absolute',
          left: `calc(50% + ${pos.x})`,
          top:  `calc(50% + ${pos.y})`,
          width: 120, height: 120,
        },
      })}
      drag
      dragConstraints={{ top: -60, bottom: 60, left: -60, right: 60 }}
      dragElastic={0.35}
      onClick={handleClick}
      initial={{ opacity: 0, scale: 0.4, y: 30 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      transition={{
        delay: 1.2 + index * 0.18,
        type: 'spring', stiffness: 260, damping: 18,
      }}
      whileHover={{ scale: 1.12, boxShadow: `0 0 28px rgba(84,227,70,0.4)` }}
      whileTap={{ scale: 0.92 }}
      aria-label={`Data shard: ${def.id}`}
      role="button"
    >
      {/* Shard card */}
      <div
        className="w-full h-full rounded-xl border flex items-center justify-center p-2 relative overflow-hidden"
        style={{
          borderColor: 'rgba(84,227,70,0.2)',
          background: 'rgba(8,203,0,0.04)',
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
        }}
      >
        {/* Scan-line inside shard */}
        <div
          className="absolute left-0 right-0 h-px pointer-events-none"
          style={{
            background: `linear-gradient(90deg, transparent, ${GREEN_B}55, transparent)`,
            animation: `scanUp 3s ${floatDelay}s ease-in-out infinite`,
          }}
        />
        {def.icon}
      </div>

      {/* Float bob */}
      <motion.div
        className="absolute inset-0 pointer-events-none"
        animate={{ y: [0, -6, 0] }}
        transition={{ duration: 3 + index * 0.4, repeat: Infinity, ease: 'easeInOut', delay: floatDelay }}
      />

      {/* Confetti burst */}
      <AnimatePresence>
        {burst && <ConfettiBurst cx={burst.x} cy={burst.y} onDone={clearBurst} />}
      </AnimatePresence>
    </motion.div>
  );
};

/* 
   CTA  "Activate Now" with energy ripple
    */
const ActivateCTA = ({ onClick }) => {
  const [ripples, setRipples] = useState([]);

  const onHover = useCallback(() => {
    const id = Date.now();
    setRipples(r => [...r.slice(-3), id]);
    setTimeout(() => setRipples(r => r.filter(x => x !== id)), 1100);
  }, []);

  return (
    <div className="relative inline-flex items-center justify-center">
      {/* Energy ripples */}
      <AnimatePresence>
        {ripples.map(id => (
          <motion.div
            key={id}
            className="absolute rounded-full pointer-events-none"
            style={{ border: `2px solid ${GREEN_B}`, width: 180, height: 60, borderRadius: 28 }}
            initial={{ scale: 1, opacity: 0.7 }}
            animate={{ scale: 2.4, opacity: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1, ease: 'easeOut' }}
            aria-hidden="true"
          />
        ))}
      </AnimatePresence>

      <motion.button
        onClick={onClick}
        onHoverStart={onHover}
        className="relative overflow-hidden rounded-full font-black
                   tracking-widest uppercase text-base
                   focus:outline-none focus-visible:ring-4
                   focus-visible:ring-[#54E346] focus-visible:ring-offset-2
                   focus-visible:ring-offset-black z-10"
        style={{
          background: `linear-gradient(135deg, ${GREEN_A} 0%, ${GREEN_B} 60%, ${GREEN_A} 100%)`,
          padding: '18px 56px',
          color: '#000',
          letterSpacing: '0.12em',
          boxShadow: `0 0 30px rgba(8,203,0,0.4), 0 0 60px rgba(8,203,0,0.12)`,
        }}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 2, type: 'spring', stiffness: 260, damping: 18 }}
        whileHover={{
          scale: 1.06,
          boxShadow: `0 0 50px rgba(84,227,70,0.65), 0 0 100px rgba(8,203,0,0.25)`,
        }}
        whileTap={{ scale: 0.93 }}
        aria-label="Launch Opticore"
      >
        {/* Shimmer */}
        <motion.span
          className="absolute inset-0 rounded-full pointer-events-none"
          style={{ background: 'linear-gradient(105deg, transparent 25%, rgba(255,255,255,0.3) 50%, transparent 75%)' }}
          animate={{ x: ['-80%', '80%'] }}
          transition={{ duration: 2.2, repeat: Infinity, ease: 'linear', repeatDelay: 1.4 }}
          aria-hidden="true"
        />
        <span className="relative z-10 flex items-center gap-2">
          Launch Opticore
          <motion.span
            animate={{ x: [0, 4, 0] }}
            transition={{ duration: 1.2, repeat: Infinity, ease: 'easeInOut' }}
            aria-hidden="true"
          ></motion.span>
        </span>
      </motion.button>
    </div>
  );
};

/* 
   STAT PILL
    */
const StatPill = ({ value, label, delay }) => (
  <motion.div
    className="flex flex-col items-center"
    initial={{ opacity: 0, y: 14 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5, delay }}
  >
    <span className="text-2xl sm:text-3xl font-black" style={{ color: GREEN_B }}>{value}</span>
    <span className="text-xs uppercase tracking-widest mt-0.5" style={{ color: 'rgba(255,255,255,0.36)' }}>
      {label}
    </span>
  </motion.div>
);

/* 
   MAIN HERO
    */
const Hero = ({ onDemo }) => {
  const sectionRef = useRef(null);
  const isMob = mob();
  const navigate = useNavigate();

  /*  mouse parallax  */
  const rawX     = useMotionValue(0);
  const rawY     = useMotionValue(0);
  const springX  = useSpring(rawX, { stiffness: 50, damping: 18 });
  const springY  = useSpring(rawY, { stiffness: 50, damping: 18 });

  const holoX     = useTransform(springX, v => v * 0.018);
  const holoY     = useTransform(springY, v => v * 0.018);
  const textX     = useTransform(springX, v => v * -0.006);
  const textY     = useTransform(springY, v => v * -0.006);

  const onMouseMove = useCallback((e) => {
    if (!sectionRef.current) return;
    const { left, top, width, height } = sectionRef.current.getBoundingClientRect();
    rawX.set(e.clientX - left - width  / 2);
    rawY.set(e.clientY - top  - height / 2);
  }, [rawX, rawY]);

  return (
    <section
      ref={sectionRef}
      id="hero"
      className="relative min-h-screen overflow-hidden flex flex-col"
      style={{
        backgroundColor: '#000',
        backgroundImage: `linear-gradient(rgba(0,0,0,0.12), rgba(0,0,0,0.12)), url(${process.env.PUBLIC_URL + '/hero_result.webp'})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        backgroundAttachment: 'scroll',
      }}
      onMouseMove={onMouseMove}
      aria-label="Hero"
    >
      {/*  Inject scan-line keyframe  */}
      <style>{`
        @keyframes scanUp {
          0%   { top: 100%; opacity: 0; }
          10%  { opacity: 1; }
          90%  { opacity: 1; }
          100% { top: 0%; opacity: 0; }
        }
      `}</style>

      {/* Logo top-left (from public/logo.svg) - positioned flush to viewport corner */}
      <Link to="/" aria-label="OpticVance home" className="absolute left-2 top-2 sm:left-4 sm:top-4 z-50">
        <img
          src="/logo.svg"
          alt="OpticVance logo"
          className="w-16 h-16 sm:w-28 sm:h-28 lg:w-40 lg:h-40"
          style={{ display: 'block', filter: 'drop-shadow(0 22px 56px rgba(8,203,0,0.22))' }}
        />
      </Link>

      {/*  Infinity grid floor  */}
      <GridFloor tiltX={springX} tiltY={springY} />

      {/*  Radial void gradient  */}
      <div
        className="absolute inset-0 pointer-events-none z-0"
        style={{ background: 'radial-gradient(ellipse 80% 70% at 50% 42%, rgba(4,14,4,0.28) 0%, rgba(0,0,0,0.48) 55%)' }}
        aria-hidden="true"
      />

      {/* 
          CENTRAL COLUMN  hologram content
           */}
      <div className="relative z-10 flex-1 flex flex-col items-center justify-center
                      px-5 sm:px-8 lg:px-0 pt-20 pb-44 lg:pt-20 lg:pb-44">

        {/*  Holographic projection zone  */}
        <motion.div
          className="relative flex flex-col items-center w-full max-w-5xl"
          style={isMob ? {} : { x: holoX, y: holoY }}
        >

          {/*  Floating data shards  */}
          {!isMob ? (
            /* Desktop: absolute positioned around centre */
            <div className="absolute inset-0 pointer-events-none z-30" aria-label="Holographic data shards">
              {SHARD_DEFS.map((def, i) => (
                <HoloShard key={def.id} def={def} index={i} isMob={false} />
              ))}
            </div>
          ) : null}

          {/*  Hologram glow cone  */}
          <div
            className="absolute left-1/2 -translate-x-1/2 bottom-0 w-96 sm:w-[30rem]"
            style={{
              height: '110%',
              background: `linear-gradient(0deg, rgba(84,227,70,0.10) 0%, rgba(84,227,70,0.03) 40%, transparent 75%)`,
              clipPath: 'polygon(35% 100%, 65% 100%, 80% 0%, 20% 0%)',
              filter: 'blur(6px)',
            }}
            aria-hidden="true"
          />

          {/*  Badge  */}
          <motion.div
            className="inline-flex items-center gap-2 rounded-full border px-4 py-1.5
                       text-xs font-semibold tracking-widest uppercase mb-6"
            style={{
              borderColor: 'rgba(8,203,0,0.5)',
              background: 'linear-gradient(90deg, rgba(8,203,0,0.12), rgba(84,227,70,0.06))',
              color: '#ffffff',
              boxShadow: '0 10px 40px rgba(8,203,0,0.12)',
              zIndex: 60,
            }}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <motion.span
              className="w-2 h-2 rounded-full flex-shrink-0"
              style={{ background: GREEN_A, boxShadow: `0 0 10px ${GREEN_B}` }}
              animate={{ scale: [1, 1.6, 1], opacity: [1, 0.4, 1] }}
              transition={{ duration: 1.5, repeat: Infinity }}
              aria-hidden="true"
            />
            Holographic AI Platform
          </motion.div>

          {/*  Headline (glitch materialisation)  */}
          <motion.div
            style={isMob ? {} : { x: textX, y: textY }}
          >
            <GlitchText
              text={HEADLINE}
              className="text-center font-black leading-tight mb-4
                         text-[clamp(2.4rem,6.6vw,4.5rem)]"
              delay={400}
            />
          </motion.div>

          {/*  Subheadline (glitch layer)  */}
          <GlitchText
            text={SUBHEAD}
            as="p"
            className="text-center leading-relaxed text-base sm:text-lg mb-8 max-w-xl mx-auto"
            delay={1200}
          />

          {/*  CTA at pedestal base  */}
          <div className="mb-6 flex flex-wrap items-center justify-center gap-4">
            <ActivateCTA onClick={() => navigate('/product')} />
            <motion.a
              href="#features"
              className="text-sm font-semibold tracking-wide hover:underline underline-offset-4 transition-colors"
              style={{ color: 'rgba(84,227,70,0.55)' }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 2.4 }}
              onClick={e => {
                e.preventDefault();
                document.querySelector('#features')?.scrollIntoView({ behavior: 'smooth' });
              }}
            >
              Explore Features 
            </motion.a>
          </div>

          {/*  Pedestal  */}
          <Pedestal />

          {/*  Mobile shards (horizontal scroll)  */}
          {isMob && (
            <div
              className="flex gap-4 overflow-x-auto px-2 py-4 -mx-2 mt-6
                         snap-x snap-mandatory scrollbar-hide"
              style={{ WebkitOverflowScrolling: 'touch' }}
              aria-label="Data shards"
            >
              {SHARD_DEFS.map((def, i) => (
                <div key={def.id} className="snap-center flex-shrink-0">
                  <HoloShard def={def} index={i} isMob={true} />
                </div>
              ))}
            </div>
          )}

          {/*  Stats strip  */}
          <motion.div
            className="flex items-center gap-6 sm:gap-8 border-t pt-6 mt-8"
            style={{ borderColor: 'rgba(255,255,255,0.07)' }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 2.8 }}
          >
            <StatPill value={'10\u00d7'}   label="Faster"   delay={2.9} />
            <div className="w-px h-8 bg-white/10" aria-hidden="true" />
            <StatPill value="99.7%"  label="Accuracy"     delay={3.1} />
            <div className="w-px h-8 bg-white/10" aria-hidden="true" />
            <StatPill value="Live"   label="Inference"    delay={3.3} />
          </motion.div>
        </motion.div>
      </div>

      {/*  Scroll indicator  */}
      <motion.div
        className="absolute bottom-40 left-1/2 -translate-x-1/2 z-20
                   flex flex-col items-center gap-1 pointer-events-none"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 3.8 }}
        aria-hidden="true"
      >
        <motion.div
          className="w-px h-10"
          style={{ background: `linear-gradient(180deg, transparent, ${GREEN_A})` }}
          animate={{ scaleY: [1, 0.3, 1], opacity: [0.4, 1, 0.4] }}
          transition={{ duration: 1.8, repeat: Infinity, ease: 'easeInOut' }}
        />
        <span className="text-[9px] uppercase tracking-[0.22em]" style={{ color: 'rgba(8,203,0,0.35)' }}>scroll</span>
      </motion.div>
    </section>
  );
};

export default Hero;